import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { EmptyTileState } from '@/components/Dashboard/EmptyTileState';
import { Calendar } from '@/components/Icons/Calendar';
import { Check } from '@/components/Icons/Check';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { Modal } from '@/components/Modal';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { RatingsOptionCard } from '@/pages/Overview/RatingsOptionCard';
import { useLessonTypes } from '@/pages/Overview/hooks/useLessonTypes';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import {
  BorderRadius,
  ButtonVariant,
  EvaluationMode,
  ModalSize,
  ModalVariant,
  Size,
} from '@/types/components';
import { tv } from '@/utils/tv';

interface Props {
  mode: EvaluationMode;
  lessonTypeId: number | null;
  onChange: (mode: EvaluationMode, lessonTypeId: number | null) => void;
  fitWidth?: boolean;
  isAlignedRight?: boolean;
}

const triggerChip = tv({
  base: 'flex w-fit cursor-pointer items-center gap-2 rounded-3xl px-3 py-2 shadow-card transition-colors',
  variants: {
    isOpen: {
      true: 'bg-background',
      false: 'bg-popover hover:bg-info-surface',
    },
    fitWidth: {
      true: '',
      false: 'min-w-[212px] justify-between',
    },
  },
});

const triggerChevron = tv({
  base: 'size-4 text-primary transition-transform duration-200',
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const option = tv({
  base: 'flex shrink-0 cursor-pointer items-center justify-between gap-2 rounded-xl py-3 pr-2 pl-3',
  variants: {
    isSelected: {
      true: 'bg-info-surface',
      false: 'hover:bg-hover',
    },
  },
});

const optionLabel = tv({
  base: 'truncate',
  variants: {
    isSelected: {
      true: 'text-subhead-14 text-primary',
      false: 'text-body-14 text-muted-foreground',
    },
  },
});

const segment = tv({
  base: 'flex flex-1 cursor-pointer items-center justify-center rounded-[20px] text-subhead-14 transition-colors',
  variants: {
    isSelected: {
      true: 'bg-primary font-medium text-primary-foreground shadow-[0_1px_4px_0_rgba(0,0,0,0.1)]',
      false: 'text-foreground',
    },
  },
});

const fieldLabel = tv({
  base: 'flex-1 truncate text-left text-subhead-14',
  variants: {
    hasValue: {
      true: 'text-foreground',
      false: 'text-muted-foreground',
    },
  },
});

export const EvaluationTypePicker: React.FC<Props> = ({
  mode,
  lessonTypeId,
  onChange,
  fitWidth = false,
  isAlignedRight = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(lessonTypeId);
  const containerRef = useRef<HTMLDivElement>(null);
  const { sheetRef, swipeProps } = useSwipeToClose(() => setIsOpen(false));
  const { data: lessonTypes = [], isLoading } = useLessonTypes({
    enabled: isOpen,
  });

  const averageLabel = t('overview.ratings.average');
  const selectedTypeName = lessonTypes.find(
    (type) => type.id === lessonTypeId,
  )?.name;
  const chipLabel =
    mode === EvaluationMode.Average
      ? averageLabel
      : (selectedTypeName ?? averageLabel);

  const isOptionSelected = (optionMode: EvaluationMode, id: number | null) =>
    optionMode === EvaluationMode.Average
      ? mode === EvaluationMode.Average
      : mode === EvaluationMode.Final && lessonTypeId === id;

  const handleSelect = (optionMode: EvaluationMode, id: number | null) => {
    setIsOpen(false);
    onChange(optionMode, id);
  };

  const openSheet = () => {
    setPendingId(lessonTypeId);
    setIsOpen(true);
  };

  const saveSheet = () => {
    setIsOpen(false);
    onChange(EvaluationMode.Final, pendingId);
  };

  useCloseOnBreakpointChange(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile]);

  const renderOption = (
    optionMode: EvaluationMode,
    id: number | null,
    label: string,
  ) => {
    const isSelected = isOptionSelected(optionMode, id);
    return (
      <button
        key={id ?? 'average'}
        type="button"
        onClick={() => handleSelect(optionMode, id)}
        className={option({ isSelected })}
      >
        <span className={optionLabel({ isSelected })}>{label}</span>
        {isSelected && <Check className="size-4 shrink-0 text-primary" />}
      </button>
    );
  };

  if (isMobile) {
    const isFinal = mode === EvaluationMode.Final;
    return (
      <>
        <div className="flex flex-col gap-4">
          <div className="flex h-10 rounded-[20px] bg-popover p-0.5 shadow-card">
            <button
              type="button"
              onClick={() => onChange(EvaluationMode.Average, null)}
              className={segment({ isSelected: !isFinal })}
            >
              {averageLabel}
            </button>
            <button
              type="button"
              onClick={() => onChange(EvaluationMode.Final, lessonTypeId)}
              className={segment({ isSelected: isFinal })}
            >
              {t('overview.ratings.final')}
            </button>
          </div>

          {isFinal && (
            <div className="flex flex-col gap-2">
              <h2 className="text-subhead-14 text-muted-foreground">
                {t('overview.ratings.evaluationType')}
              </h2>
              <button
                type="button"
                onClick={openSheet}
                className="flex cursor-pointer items-center gap-3 rounded-xl bg-popover px-4 py-3 shadow-card"
              >
                <span className="flex items-center justify-center rounded-full bg-info-surface p-2 text-primary">
                  <Calendar className="size-4" />
                </span>
                <span className={fieldLabel({ hasValue: !!selectedTypeName })}>
                  {selectedTypeName ?? t('overview.ratings.chooseType')}
                </span>
                <ChevronDown className="size-4 shrink-0 text-primary" />
              </button>
            </div>
          )}
        </div>

        <Modal
          isOpen={isOpen}
          label={t('overview.ratings.evaluationType')}
          variant={ModalVariant.BottomSheet}
          size={ModalSize.Small}
          contentRef={sheetRef}
          onClose={() => setIsOpen(false)}
        >
          <div
            {...swipeProps}
            className="flex shrink-0 cursor-grab flex-col touch-none active:cursor-grabbing"
          >
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1 w-10 rounded-full bg-drag-handle" />
            </div>
            <div className="flex items-center px-4 pt-3 pb-3">
              <h2 className="text-title-30 text-foreground">
                {t('overview.ratings.evaluationType')}
              </h2>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4">
            {isLoading && (
              <div className="flex flex-1 items-center justify-center py-8">
                <PageSpinner className="size-[20px] text-subtle-foreground" />
              </div>
            )}
            {!isLoading && lessonTypes.length === 0 && <EmptyTileState />}
            {!isLoading &&
              lessonTypes.map((type) => (
                <RatingsOptionCard
                  key={type.id}
                  title={type.name}
                  hasIcon
                  isSelected={pendingId === type.id}
                  onSelect={() => setPendingId(type.id)}
                />
              ))}
          </div>
          <div className="shrink-0 px-4 pt-3 pb-[calc(16px+env(safe-area-inset-bottom))]">
            <Button
              variant={ButtonVariant.Primary}
              size={Size.Medium}
              radius={BorderRadius.Small}
              className="h-14 w-full font-semibold"
              onClick={saveSheet}
            >
              {t('overview.ratings.save')}
            </Button>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={triggerChip({ isOpen, fitWidth })}
      >
        <span className="text-caption-12 text-primary">{chipLabel}</span>
        <ChevronDown className={triggerChevron({ isOpen })} />
      </button>

      {isOpen && (
        <div
          className={`absolute top-full z-10 mt-2 flex max-h-[360px] w-[280px] flex-col gap-3 overflow-y-auto rounded-[14px] bg-popover p-2 shadow-card ${isAlignedRight ? 'right-0' : 'left-0'}`}
        >
          {renderOption(EvaluationMode.Average, null, averageLabel)}

          {lessonTypes.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="py-1 pr-2 text-caption-12 text-muted-foreground">
                {t('overview.ratings.final')}
              </p>
              {lessonTypes.map((type) =>
                renderOption(EvaluationMode.Final, type.id, type.name),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
